import { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Button,
  Card,
  Group,
  Badge,
  Textarea,
  Radio,
  Alert,
  Divider,
  List,
  Anchor,
} from "@mantine/core";
import { IconCheck, IconX, IconAlertCircle, IconDownload } from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { api } from "../api";
import dayjs from "dayjs";
import { Logo } from "../components/Logo";

interface Evaluation {
  id: string;
  tender: {
    id: string;
    description: string;
    referenceNo: string;
  };
  bidSubmission: {
    id: string;
    companyName: string;
    submittedAt: string;
    documents: Array<{
      id: string;
      fileName: string;
      documentType: string;
    }>;
  };
  status: string;
  evaluationNotes?: string;
  isApproved?: boolean;
}

export function Evaluate() {
  const { token } = useParams<{ token: string }>();
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    isApproved: undefined as boolean | undefined,
    evaluationNotes: "",
    rejectionReason: "",
  });

  useEffect(() => {
    if (token) {
      fetchEvaluation();
    }
  }, [token]);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/public/evaluations/${token}`);
      setEvaluation(response.data);
      
      // Pre-fill form if already has data
      if (response.data) {
        setFormData({
          isApproved: response.data.isApproved,
          evaluationNotes: response.data.evaluationNotes || "",
          rejectionReason: response.data.rejectionReason || "",
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load evaluation");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (formData.isApproved === undefined) {
      setError("Please select Approve or Reject");
      return;
    }

    if (!formData.isApproved && !formData.rejectionReason.trim()) {
      setError("Please provide a rejection reason");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await api.post(`/public/evaluations/${token}/submit`, {
        isApproved: formData.isApproved,
        evaluationNotes: formData.evaluationNotes || undefined,
        rejectionReason: formData.rejectionReason || undefined,
      });

      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to submit evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  const getDocumentDownloadUrl = (documentId: string) => {
    return `/api/public/bid-submission-documents/${documentId}/download?token=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
        <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
          <Container size="lg" className="py-4">
            <Group gap="md">
              <Logo height={48} />
              <Title order={1} size="h3" c="green.6">
                HMH Procurement System
              </Title>
            </Group>
          </Container>
        </header>
        <Container size="md" py="xl">
          <Paper shadow="sm" p="xl">
            <Text>Loading evaluation...</Text>
          </Paper>
        </Container>
      </div>
    );
  }

  if (error && !evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
        <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
          <Container size="lg" className="py-4">
            <Group gap="md">
              <Logo height={48} />
              <Title order={1} size="h3" c="green.6">
                HMH Procurement System
              </Title>
            </Group>
          </Container>
        </header>
        <Container size="md" py="xl">
          <Paper shadow="sm" p="xl">
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              {error}
            </Alert>
          </Paper>
        </Container>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
        <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
          <Container size="lg" className="py-4">
            <Group gap="md">
              <Logo height={48} />
              <Title order={1} size="h3" c="green.6">
                HMH Procurement System
              </Title>
            </Group>
          </Container>
        </header>
        <Container size="md" py="xl">
          <Paper shadow="sm" p="xl">
            <Stack gap="md" align="center">
              <IconCheck size={48} color="green" />
              <Title order={2}>Evaluation Submitted Successfully</Title>
              <Text c="dimmed">
                Thank you for your evaluation. Your response has been recorded.
              </Text>
              {evaluation && (
                <Card shadow="sm" p="md" withBorder>
                  <Stack gap="xs">
                    <Text size="sm">
                      <strong>Tender:</strong> {evaluation.tender.description}
                    </Text>
                    <Text size="sm">
                      <strong>Reference:</strong> {evaluation.tender.referenceNo}
                    </Text>
                    <Text size="sm">
                      <strong>Bidder:</strong> {evaluation.bidSubmission.companyName}
                    </Text>
                    <Text size="sm">
                      <strong>Decision:</strong>{" "}
                      <Badge color={formData.isApproved ? "green" : "red"}>
                        {formData.isApproved ? "Approved" : "Rejected"}
                      </Badge>
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Paper>
        </Container>
      </div>
    );
  }

  if (!evaluation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
      <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <Container size="lg" className="py-4">
          <Group gap="md">
            <Logo height={48} />
            <Title order={1} size="h3" c="green.6">
              HMH Procurement System
            </Title>
          </Group>
        </Container>
      </header>
      <Container size="md" py="xl">
        <Paper shadow="sm" p="xl">
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">Technical Evaluation</Title>
            <Text c="dimmed" size="sm">
              Please review the bid submission and provide your evaluation.
            </Text>
          </div>

          <Card shadow="sm" p="md" withBorder>
            <Title order={4} mb="md">Tender Information</Title>
            <Stack gap="xs">
              <Text size="sm">
                <strong>Description:</strong> {evaluation.tender.description}
              </Text>
              <Text size="sm">
                <strong>Reference Number:</strong> {evaluation.tender.referenceNo}
              </Text>
            </Stack>
          </Card>

          <Card shadow="sm" p="md" withBorder>
            <Title order={4} mb="md">Bid Submission Information</Title>
            <Stack gap="xs">
              <Text size="sm">
                <strong>Company Name:</strong> {evaluation.bidSubmission.companyName}
              </Text>
              <Text size="sm">
                <strong>Submitted At:</strong>{" "}
                {dayjs(evaluation.bidSubmission.submittedAt).format("MMM DD, YYYY HH:mm")}
              </Text>
            </Stack>
          </Card>

          <Card shadow="sm" p="md" withBorder>
            <Title order={4} mb="md">Technical Documents</Title>
            {evaluation.bidSubmission.documents && evaluation.bidSubmission.documents.length > 0 ? (
              <List>
                {evaluation.bidSubmission.documents.map((doc) => (
                  <List.Item key={doc.id}>
                    <Group gap="xs">
                      <IconDownload size={16} />
                      <Anchor
                        href={getDocumentDownloadUrl(doc.id)}
                        target="_blank"
                        download
                        style={{ fontWeight: 500 }}
                      >
                        {doc.fileName}
                      </Anchor>
                      <Badge size="xs" variant="light" color="blue">
                        {doc.documentType.replace(/_/g, " ")}
                      </Badge>
                    </Group>
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                No technical documents available for this bid submission.
              </Text>
            )}
          </Card>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              {error}
            </Alert>
          )}

          <Divider />

          <Card shadow="sm" p="md" withBorder>
            <Title order={4} mb="md">Evaluation Form</Title>
            <Stack gap="md">
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Decision <span style={{ color: "red" }}>*</span>
                </Text>
                <Radio.Group
                  value={formData.isApproved?.toString()}
                  onChange={(value) =>
                    setFormData({ ...formData, isApproved: value === "true" })
                  }
                >
                  <Stack gap="xs">
                    <Radio
                      value="true"
                      label="Approve"
                      icon={IconCheck}
                    />
                    <Radio
                      value="false"
                      label="Reject"
                      icon={IconX}
                    />
                  </Stack>
                </Radio.Group>
              </div>

              {formData.isApproved === false && (
                <Textarea
                  label="Rejection Reason"
                  placeholder="Please provide a reason for rejection"
                  value={formData.rejectionReason}
                  onChange={(e) =>
                    setFormData({ ...formData, rejectionReason: e.target.value })
                  }
                  required
                  rows={3}
                />
              )}

              <Textarea
                label="Evaluation Notes (Optional)"
                placeholder="Add any additional notes or comments"
                value={formData.evaluationNotes}
                onChange={(e) =>
                  setFormData({ ...formData, evaluationNotes: e.target.value })
                }
                rows={4}
              />
            </Stack>
          </Card>

          <Group justify="flex-end">
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={formData.isApproved === undefined}
              size="lg"
            >
              Submit Evaluation
            </Button>
          </Group>
        </Stack>
      </Paper>
      </Container>
    </div>
  );
}

